import React from "react";
import GlobalSearch from "./components/GlobalSearch";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
// ClientesPage — reemplazada por CatastroPage (redirect en rutas)
import CatastroPage from "./pages/CatastroPage";
import UsuariosPage from "./pages/UsuariosPage";
import ConfigGlobalPage from "./pages/ConfigGlobalPage";
import PerfilPage from "./pages/PerfilPage";
import AuditoriaPage from "./pages/AuditoriaPage";
import InventarioPage from "./pages/InventarioPage";
import ReportesPage from "./pages/ReportesPage";
// ProveedoresPage — reemplazada por CatastroPage (redirect en rutas)
import CostosFijosPage from "./pages/CostosFijosPage";
// EmpleadosPage — reemplazada por CatastroPage (redirect en rutas)
import BalancePage from "./pages/BalancePage";
import IngresoVarioPage from "./pages/IngresoVarioPage";
import EgresosPage from "./pages/EgresosPage";
import VentasPage from "./pages/VentasPage";
import PDVPage from "./pages/PDVPage";
import ProductosPage from "./pages/ProductosPage";
import StockHistorialPage from "./pages/StockHistorialPage";
import BancosPage from "./pages/BancosPage";
import PlanCuentasPage from "./pages/PlanCuentasPage";
import EmpresasPropiasPage from "./pages/EmpresasPropiasPage";
import SoportePage from "./pages/SoportePage";
import FinancieroPage from "./pages/FinancieroPage";
import LicenciasPage from "./pages/LicenciasPage";
import LecturaBombasPage from "./pages/LecturaBombasPage";
import ProduccionPage from "./pages/ProduccionPage";
import ChequesPage from "./pages/ChequesPage";
import HotelPage from "./pages/HotelPage";
import LoteadoraPage from "./pages/LoteadoraPage";
import TransportePage from "./pages/TransportePage";
import TallerPage from "./pages/TallerPage";
import BasculaPage from "./pages/BasculaPage";
import ImportacionesPage from "./pages/ImportacionesPage";
import EquiposBasculaPage from "./pages/EquiposBasculaPage";
import RecetarioPage from "./pages/RecetarioPage";
import { rutaModuloUnico } from "./lib/moduloUnico";
import ClinicaPage from "./pages/ClinicaPage";
import ConstructoraPage from "./pages/ConstructoraPage";
import RestaurantePage from "./pages/RestaurantePage";
import { toast } from "sonner";
import { empresaTieneModulo, permisoHabilitadoPorEmpresa } from "./lib/modulosEmpresa";
import { useInactivityLogout } from "./hooks/useInactivityLogout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ARANDUJAR_TOKEN_KEY = "arandujar_token";

// Empresa activa en pantalla: se envía en TODAS las llamadas al backend (cabecera
// X-Empresa-Activa) para que Auditoría registre en qué empresa se hizo cada acción,
// también para usuarios que pertenecen a varias empresas.
let __empresaActivaSlug = "";
if (typeof window !== "undefined" && !window.__empresaFetchPatched) {
  window.__empresaFetchPatched = true;
  const _fetchOriginal = window.fetch.bind(window);
  window.fetch = (input, init) => {
    try {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      if (__empresaActivaSlug && url.startsWith(API)) {
        const headers = new Headers((init && init.headers) || (typeof input !== "string" && input && input.headers) || {});
        if (!headers.has("X-Empresa-Activa")) headers.set("X-Empresa-Activa", __empresaActivaSlug);
        return _fetchOriginal(input, { ...(init || {}), headers });
      }
    } catch (e) { /* si algo falla, sigue sin la cabecera */ }
    return _fetchOriginal(input, init);
  };
}
const CLINIC_TOKEN_KEY = "arandu_clinic_token";
const CLINIC_DOCTOR_KEY = "arandu_clinic_doctor";
const isAdminRole = (role) => role === "admin" || role === "super_admin";

// ── Auth + Theme context ────────────────────────────────────────────────────
export const AuthContext = React.createContext(null);

// Aplica el tema de la empresa activa al elemento raíz del admin
// IMPORTANTE: solo aplica cuando el usuario está en /sistema/*; en / (landing) y /login
// mantiene el tema oscuro-azul por defecto para no "romper" el branding público.
function applyTheme(tema) {
  const el = document.getElementById("arandu-admin-root");
  if (!el) return;
  const temas = ["oscuro-azul", "oscuro-rojo", "claro-rojo", "claro-dorado", "claro-azul"];
  temas.forEach(t => el.removeAttribute("data-tema"));

  const onAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/sistema");

  if (onAdmin && tema && tema !== "oscuro-azul") {
    el.setAttribute("data-tema", tema);
  }
  // Para temas claros también cambiamos el body bg — solo si estamos en /sistema
  const lightBgs = { "claro-rojo": "#f8fafc", "claro-dorado": "#fefce8", "claro-azul": "#f0f9ff" };
  document.body.style.backgroundColor = onAdmin ? (lightBgs[tema] || "#0a1628") : "#0a1628";
}

function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [token, setToken] = React.useState(localStorage.getItem(ARANDUJAR_TOKEN_KEY));
  const [loading, setLoading] = React.useState(true);
  const [empresasPropias, setEmpresasPropias] = React.useState([]);
  const [activeEmpresaPropia, setActiveEmpresaPropia] = React.useState(null);
  __empresaActivaSlug = activeEmpresaPropia?.slug || "";
  const [empresasLoading, setEmpresasLoading] = React.useState(false);
  const [empresasResolved, setEmpresasResolved] = React.useState(false);
  const [empresaResolveError, setEmpresaResolveError] = React.useState(false);
  // Control de licencia — global (no depende de qué página esté abierta), así
  // usuarios restringidos que navegan directo a un módulo también lo reciben.
  const [licenciaEstado, setLicenciaEstado] = React.useState(null); // null = sin chequear aún

  // Cargar empresas propias y detectar la activa del usuario
  const loadEmpresasPropias = React.useCallback(async (savedToken, userData) => {
    setEmpresasLoading(true);
    setEmpresasResolved(false);
    setEmpresaResolveError(false);
    try {
      const res = await fetch(`${API}/admin/empresas-propias`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      if (res.ok) {
        const propias = await res.json();
        setEmpresasPropias(propias);

        // Determinar empresa activa:
        // 1. Usar empresa_default guardada (localStorage o perfil)
        // 2. Si no, usar primera empresa asignada
        const savedDefault = localStorage.getItem("empresa_default") || userData?.empresa_default;

        if (propias.length > 0) {
          let found = null;
          // Normalizar a string para evitar type mismatch (ObjectId vs string)
          const logosAsignados = (userData.logos_asignados || []).map(String);

          // Intentar con la empresa_default guardada — solo si está en la lista retornada por
          // el backend (que ya filtró por logos_asignados del usuario). Esto evita que un
          // empresa_default stale de otro usuario/sesión anterior dé acceso incorrecto.
          if (savedDefault) {
            found = propias.find(p => String(p.id) === String(savedDefault));
            // Para usuarios no-admin: verificar que la empresa esté en sus logos_asignados.
            // Evita que un empresa_default stale (de otro usuario, sesión anterior, o dato
            // obsoleto en el backend) le asigne una empresa a la que no tiene acceso.
            if (found && userData?.role !== "admin" && userData?.role !== "super_admin") {
              if (!logosAsignados.includes(String(found.id))) {
                found = null;
              }
            }
            if (!found) localStorage.removeItem("empresa_default");
          }

          // Si el usuario no es admin/super_admin: preferir empresa_default; si no hay, usar la 1ra asignada
          if (!found && !isAdminRole(userData?.role)) {
            const accesibles = propias.filter(p =>
              logosAsignados.includes(String(p.id))
            );
            // Usuario con 1 sola empresa asignada → auto-seleccionarla
            if (accesibles.length >= 1) {
              found = accesibles[0];
            }
          }

          // Admins/super_admins ven todo, pero la UI necesita una empresa activa
          // para resolver logo, tema y módulos sin caer en el fallback legacy.
          if (!found && isAdminRole(userData?.role)) {
            found = (savedDefault && propias.find(p => String(p.id) === String(savedDefault))) || propias[0];
          }

          if (found) {
            setActiveEmpresaPropia(found);
            applyTheme(found.tema || "oscuro-azul");
          } else {
            setActiveEmpresaPropia(null);
            applyTheme("oscuro-azul");
          }
        } else {
          setActiveEmpresaPropia(null);
          applyTheme("oscuro-azul");
        }
      } else {
        setEmpresasPropias([]);
        setActiveEmpresaPropia(null);
        applyTheme("oscuro-azul");
        setEmpresaResolveError(true);
      }
    } catch (e) {
      console.error("Error loading empresas propias:", e);
      setEmpresasPropias([]);
      setActiveEmpresaPropia(null);
      applyTheme("oscuro-azul");
      setEmpresaResolveError(true);
    } finally {
      setEmpresasLoading(false);
      setEmpresasResolved(true);
    }
  }, []);

  React.useEffect(() => {
    const checkAuth = async () => {
      // ── Version check: invalida caches ard_* si el backend cambió de versión ──
      // Corre en cada arranque de la app; si la versión del backend cambió,
      // borra todos los keys de localStorage con prefijo "ard_" (datos cacheados)
      // para que el usuario reciba datos frescos automáticamente.
      try {
        const vRes = await fetch(`${API}/version`);
        if (vRes.ok) {
          const { version } = await vRes.json();
          const storedVersion = localStorage.getItem("arandu_app_version");
          if (storedVersion !== version) {
            Object.keys(localStorage)
              .filter(k => k.startsWith("ard_"))
              .forEach(k => localStorage.removeItem(k));
            localStorage.setItem("arandu_app_version", version);
          }
        }
      } catch (_) { /* sin red — no interferir con el flujo normal */ }

      const savedToken = localStorage.getItem(ARANDUJAR_TOKEN_KEY);
      if (savedToken) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(savedToken);
            await loadEmpresasPropias(savedToken, userData);
          } else {
            localStorage.removeItem(ARANDUJAR_TOKEN_KEY);
            setToken(null);
            setEmpresasPropias([]);
            setActiveEmpresaPropia(null);
            setEmpresaResolveError(false);
            setEmpresasResolved(true);
          }
        } catch (e) {
          console.error("Auth check failed:", e);
          setEmpresasResolved(true);
        }
      } else {
        setEmpresasResolved(true);
      }
      setLoading(false);
    };
    checkAuth();
  }, []); // eslint-disable-line

  const login = async (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem(ARANDUJAR_TOKEN_KEY, accessToken);
    localStorage.removeItem("token");
    localStorage.removeItem(CLINIC_TOKEN_KEY);
    localStorage.removeItem(CLINIC_DOCTOR_KEY);
    // Limpiar empresa_default del localStorage al loguearse, para que no se herede
    // la selección de otro usuario o de una sesión anterior en el mismo navegador.
    // El backend provee el empresa_default correcto vía userData.empresa_default.
    localStorage.removeItem("empresa_default");
    await loadEmpresasPropias(accessToken, userData);
  };

  const logout = React.useCallback(async (silent = false) => {
    // Le avisamos al backend para que limpie la sesión activa (best-effort).
    const t = localStorage.getItem(ARANDUJAR_TOKEN_KEY);
    if (t) {
      try {
        const slug = activeEmpresaPropia?.slug || "";
        const url = slug
          ? `${API}/auth/logout?logo_tipo=${encodeURIComponent(slug)}`
          : `${API}/auth/logout`;
        await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${t}` },
        });
      } catch (e) { /* sin red, no pasa nada */ }
    }
    setUser(null);
    setToken(null);
    setActiveEmpresaPropia(null);
    setEmpresasPropias([]);
    setEmpresasLoading(false);
    setEmpresasResolved(false);
    setEmpresaResolveError(false);
    localStorage.removeItem(ARANDUJAR_TOKEN_KEY);
    localStorage.removeItem("token");
    localStorage.removeItem("empresa_default");
    applyTheme("oscuro-azul");
    if (!silent && typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }, [activeEmpresaPropia]);

  // Sesión única — chequeo periódico contra /auth/me.
  // Si el backend nos echa (token inválido o sesión cerrada en otro lado),
  // mostramos un aviso y cerramos sesión local.
  React.useEffect(() => {
    if (!token) return;
    const check = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          let detail = "";
          try { const j = await res.json(); detail = j?.detail || ""; } catch (_) {}
          if (/otro dispositivo|sesión|expir/i.test(detail)) {
            toast.error(detail || "Tu sesión fue cerrada.");
          }
          logout(true);
          if (typeof window !== "undefined") window.location.href = "/login";
        }
      } catch (e) { /* sin conexión, ignoramos */ }
    };
    const id = setInterval(check, 30000);  // cada 30 segundos
    return () => clearInterval(id);
  }, [token, logout]);

  // Cambiar empresa activa y guardar como predeterminada
  const switchEmpresa = React.useCallback((empresa) => {
    setActiveEmpresaPropia(empresa);
    applyTheme(empresa?.tema || "oscuro-azul");
    const t = localStorage.getItem(ARANDUJAR_TOKEN_KEY);
    if (empresa) {
      localStorage.setItem("empresa_default", empresa.id);
      // Persistir empresa default en el backend
      if (t) {
        fetch(`${API}/auth/empresa-default`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
          body: JSON.stringify({ empresa_id: empresa.id })
        }).catch(() => {});
      }
    } else {
      localStorage.removeItem("empresa_default");
    }
    // Registrar cambio de empresa en auditoría
    if (t) {
      fetch(`${API}/auth/switch-empresa`, {
        method: "POST",
        headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          logo_tipo: empresa?.slug || "",
          nombre: empresa?.nombre || "Todas las empresas",
        })
      }).catch(() => {});
    }
  }, []);

  const hasPermission = (permiso) => {
    if (!user) return false;
    if (user.role === "super_admin") return true; // super_admin bypasea todo
    if (!permisoHabilitadoPorEmpresa(activeEmpresaPropia, permiso)) return false;
    if (user.role === "admin" || user.role === "super_admin" || user.role === "gerente") return true;
    return (user.permisos || []).includes(permiso);
  };

  const hasModule = (modulo) => empresaTieneModulo(activeEmpresaPropia, modulo);

  const canAccessEmpresa = (empresaId) => {
    if (!user) return false;
    if (isAdminRole(user.role)) return true;
    const asignadas = user.empresas_asignadas || [];
    if (!asignadas.length) return false;
    return asignadas.includes(empresaId);
  };

  // Recargar empresas propias (llamado después de crear/editar una)
  // Mantiene la empresa activa actual, solo actualiza sus datos con la versión fresca de la API
  const refreshEmpresasPropias = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/admin/empresas-propias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const propias = await res.json();
        setEmpresasPropias(propias);
        // Si hay una empresa activa, actualizarla con los datos frescos (incluye el nuevo tema)
        setActiveEmpresaPropia(current => {
          if (!current) return current;
          const fresh = propias.find(p => p.id === current.id);
          if (fresh) {
            // Aplicar el tema actualizado
            applyTheme(fresh.tema || "oscuro-azul");
            return fresh;
          }
          return current;
        });
      }
    } catch (e) { console.error("Error al refrescar empresas:", e); }
  }, [token]);

  // Chequeo de licencia — global, corre para cualquier página bajo /sistema
  // (antes solo vivía en AdminDashboard.jsx, así que un usuario restringido que
  // navegaba directo a su módulo asignado nunca lo veía).
  const refreshLicencia = React.useCallback(async () => {
    if (!token || !user?.role) { setLicenciaEstado(null); return; }
    if (["admin", "super_admin", "gerente"].includes(user.role)) { setLicenciaEstado({ ok: true, admin: true }); return; }
    const slug = activeEmpresaPropia?.slug;
    try {
      const q = slug ? `?logo_tipo=${slug}` : "";
      const r = await fetch(`${API}/cliente/mi-licencia${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setLicenciaEstado(r.ok ? await r.json() : { ok: true });
    } catch {
      setLicenciaEstado({ ok: true });
    }
  }, [token, user?.role, activeEmpresaPropia?.slug]);

  React.useEffect(() => { refreshLicencia(); }, [refreshLicencia]);

  // Autogestión: sumar los días de gracia una única vez (permiso licencias.autoextender)
  const autoextenderLicencia = React.useCallback(async () => {
    if (!token) return { ok: false, error: "Sin sesión" };
    try {
      const q = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
      const r = await fetch(`${API}/cliente/licencia/autoextender${q}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: data.detail || "No se pudo extender la licencia" };
      await refreshLicencia();
      return { ok: true, ...data };
    } catch {
      return { ok: false, error: "No se pudo extender la licencia" };
    }
  }, [token, activeEmpresaPropia?.slug, refreshLicencia]);

  return (
    <AuthContext.Provider value={{
      user, token, login, logout, loading, hasPermission, canAccessEmpresa,
      hasModule,
      empresasPropias, activeEmpresaPropia, setActiveEmpresaPropia,
      empresasLoading, empresasResolved, empresaResolveError,
      switchEmpresa, refreshEmpresasPropias,
      licenciaEstado, refreshLicencia, autoextenderLicencia,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function SistemaLoading({ text = "Cargando..." }) {
  return (
    <div className="min-h-screen bg-arandu-dark flex items-center justify-center">
      <div className="text-arandu-blue-light animate-pulse font-heading text-2xl">{text}</div>
    </div>
  );
}

function EmpresaResolutionIssue({ logout }) {
  return (
    <div className="min-h-screen bg-arandu-dark flex items-center justify-center p-6">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl p-6 max-w-md w-full text-center shadow-2xl">
        <h2 className="text-white font-heading text-xl mb-2">No se pudo resolver la empresa activa</h2>
        <p className="text-slate-400 text-sm mb-5">
          La sesión está activa, pero el sistema no terminó de determinar a qué empresa debe entrar este usuario.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 px-4 py-2.5 rounded-xl bg-arandu-blue text-white text-sm font-medium hover:bg-arandu-blue-light transition-colors"
          >
            Reintentar
          </button>
          <button
            onClick={() => logout(false)}
            className="flex-1 px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 text-sm hover:bg-white/5 transition-colors"
          >
            Salir
          </button>
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false, requiredPermission = null, anyPermission = null }) {
  const {
    token, loading, user, hasPermission, logout,
    empresasLoading, empresasResolved, empresaResolveError, empresasPropias, activeEmpresaPropia,
  } = React.useContext(AuthContext);

  if (loading) {
    return <SistemaLoading />;
  }

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (empresasLoading || !empresasResolved) {
    return <SistemaLoading text="Validando empresa..." />;
  }

  if (empresaResolveError) {
    return <EmpresaResolutionIssue logout={logout} />;
  }

  const tieneEmpresas = Array.isArray(empresasPropias) && empresasPropias.length > 0;
  const requiereEmpresaActiva = tieneEmpresas || !isAdminRole(user.role);
  if (requiereEmpresaActiva && !activeEmpresaPropia) {
    return <EmpresaResolutionIssue logout={logout} />;
  }

  if (adminOnly && user.role !== "admin" && user.role !== "super_admin" && user.role !== "gerente") {
    return <Navigate to="/sistema" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/sistema" replace />;
  }

  if (Array.isArray(anyPermission) && anyPermission.length && !anyPermission.some(p => hasPermission(p))) {
    return <Navigate to="/sistema" replace />;
  }

  return children;
}

// Observa la ruta y re-aplica el tema correcto cuando entramos/salimos del admin
function ThemeWatcher() {
  const location = useLocation();
  const { activeEmpresaPropia } = React.useContext(AuthContext) || {};
  React.useEffect(() => {
    const tema = activeEmpresaPropia?.tema || "oscuro-azul";
    applyTheme(tema);
  }, [location.pathname, activeEmpresaPropia]);
  return null;
}

// ── Guardia de inactividad ──────────────────────────────────────────────────
// Cierra la sesión automáticamente después de INACTIVITY_MINUTES de inactividad.
// Muestra un modal de advertencia WARNING_MINUTES antes del cierre.
const INACTIVITY_MINUTES = 240; // 4 horas
const WARNING_MINUTES    = 5;   // aviso 5 minutos antes

function InactivityGuard() {
  const { token, logout } = React.useContext(AuthContext);

  const handleLogout = React.useCallback(() => {
    logout(true);
    toast.warning("Tu sesión fue cerrada por inactividad.");
    if (typeof window !== "undefined") window.location.href = "/login";
  }, [logout]);

  // Tiempos configurables en Configuración global (por defecto, los de siempre).
  const [tiempos, setTiempos] = React.useState({ inactividad: INACTIVITY_MINUTES, aviso: WARNING_MINUTES });
  React.useEffect(() => {
    if (!token) return;
    fetch(`${API}/admin/config-global/sesion`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(c => {
        if (c && Number(c.inactividad_minutos) > 0) {
          setTiempos({ inactividad: Number(c.inactividad_minutos), aviso: Number(c.aviso_inactividad_minutos) || WARNING_MINUTES });
        }
      })
      .catch(() => {});
  }, [token]);

  const { showWarning, secondsLeft, keepAlive, forceLogout } = useInactivityLogout({
    timeoutMs: tiempos.inactividad * 60 * 1000,
    warningMs: tiempos.aviso * 60 * 1000,
    onLogout: handleLogout,
    enabled: !!token,
  });

  if (!showWarning) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
        <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-400/20 flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
        </div>
        <h2 className="text-white font-bold text-lg mb-1">Sesión por cerrarse</h2>
        <p className="text-slate-400 text-sm mb-5">
          No se detectó actividad. La sesión se cerrará en:
        </p>
        <div className="text-5xl font-mono font-bold text-amber-400 mb-6 tabular-nums">
          {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </div>
        <div className="flex gap-3">
          <button
            onClick={forceLogout}
            className="flex-1 px-4 py-2.5 rounded-xl border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 transition-colors text-sm"
          >
            Cerrar sesión
          </button>
          <button
            onClick={keepAlive}
            className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold transition-colors text-sm"
          >
            Seguir conectado
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Guardia de licencia ──────────────────────────────────────────────────────
// Global: corre en cualquier página bajo /sistema (no solo en el Dashboard),
// así un usuario restringido que navega directo a su módulo asignado también
// recibe el aviso/bloqueo. Antes este chequeo solo vivía dentro de AdminDashboard.jsx.
function LicenciaGate() {
  const location = useLocation();
  const { token, user, logout, licenciaEstado, autoextenderLicencia } = React.useContext(AuthContext) || {};
  const [avisoAceptado, setAvisoAceptado] = React.useState(false);
  const [extendiendo, setExtendiendo] = React.useState(false);

  const avisoKey = `${licenciaEstado?.fecha_vencimiento || ""}|${licenciaEstado?.dias_restantes ?? ""}|${licenciaEstado?.bloqueada ? "b" : "ok"}`;
  React.useEffect(() => { setAvisoAceptado(false); }, [avisoKey]);

  const enSistema = location.pathname.startsWith("/sistema");
  if (!enSistema || !token || !user || !licenciaEstado) return null;

  const ShieldIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12l2 2 3-3.5" />
    </svg>
  );

  // ── Bloqueo total (manual, emergencia o vencimiento) ──
  if (licenciaEstado.bloqueada) {
    const puedeAutoextender = licenciaEstado.motivo === "vencimiento" && licenciaEstado.autoextension_disponible;
    const handleExtender = async () => {
      setExtendiendo(true);
      const res = await autoextenderLicencia();
      setExtendiendo(false);
      if (res.ok) toast.success(`Se sumaron ${licenciaEstado.autoextension_dias || 2} días a tu licencia.`);
      else toast.error(res.error || "No se pudo extender la licencia");
    };
    return (
      <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6">
        <div className="text-center max-w-md bg-arandu-dark-light border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
            <ShieldIcon className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-red-400 mb-3">Acceso bloqueado</h1>
          <p className="text-slate-400 leading-relaxed">{licenciaEstado.mensaje || "El acceso a esta empresa está bloqueado. Contactá al administrador del sistema."}</p>
          <div className="mt-8 flex flex-col items-center gap-3">
            {puedeAutoextender && (
              <button
                onClick={handleExtender}
                disabled={extendiendo}
                className="w-full px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-semibold transition-colors text-sm"
              >
                {extendiendo ? "Activando..." : `Activar ${licenciaEstado.autoextension_dias || 2} días adicionales (única vez)`}
              </button>
            )}
            <p className="text-xs text-slate-500">Si sos administrador del sistema, podés ingresar con tu cuenta de admin.</p>
            <button
              onClick={logout}
              className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-sm transition-colors"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Modal obligatorio de "por vencer" — hay que Aceptar para seguir ──
  if (licenciaEstado.por_vencer && !avisoAceptado) {
    const urgente = licenciaEstado.dias_restantes === 0;
    return (
      <div className="fixed inset-0 z-[9996] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
        <div className="max-w-md w-full bg-arandu-dark-light border border-white/10 rounded-2xl p-8 text-center shadow-2xl">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 border ${urgente ? "bg-red-500/15 border-red-400/20" : "bg-amber-500/15 border-amber-400/20"}`}>
            <ShieldIcon className={`w-8 h-8 ${urgente ? "text-red-400" : "text-amber-400"}`} />
          </div>
          <h2 className="text-white font-bold text-lg mb-2">Aviso de licencia</h2>
          <p className={`text-sm mb-6 leading-relaxed ${urgente ? "text-red-300" : "text-amber-300"}`}>{licenciaEstado.mensaje}</p>
          <button
            onClick={() => setAvisoAceptado(true)}
            className={`w-full px-4 py-2.5 rounded-xl font-semibold transition-colors text-sm text-black ${urgente ? "bg-red-500 hover:bg-red-400" : "bg-amber-500 hover:bg-amber-400"}`}
          >
            Aceptar
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// Usuario en modo "módulo único": solo puede estar en su módulo (más un botón para cerrar sesión).
function ModuloUnicoGuard() {
  const { user, token, logout } = React.useContext(AuthContext);
  const loc = useLocation();
  const ruta = rutaModuloUnico(user?.modulo_unico);
  if (!token || !user || !ruta) return null;
  if (!(loc.pathname === ruta || loc.pathname.startsWith(ruta + "/"))) return <Navigate to={ruta} replace />;
  // Restaurante ya trae su propio botón de salida en el encabezado
  if (user.modulo_unico === "restaurante") return null;
  return (
    <button
      onClick={() => logout()}
      className="fixed bottom-3 left-3 z-40 px-3 py-1.5 text-xs bg-black/60 hover:bg-black/80 text-slate-300 hover:text-white rounded-lg border border-white/10"
      data-testid="kiosco-logout"
    >
      Cerrar sesión
    </button>
  );
}

function App() {
  return (
    <AuthProvider>
      {/* data-tema se aplica por JS en applyTheme() */}
      <div id="arandu-admin-root" className="App min-h-screen bg-arandu-dark">
        <Toaster
          position="bottom-right"
          theme="dark"
          closeButton
          offset={24}
          visibleToasts={4}
        />
        <InactivityGuard />
        <BrowserRouter>
          <ThemeWatcher />
          <LicenciaGate />
          <ModuloUnicoGuard />
          <GlobalSearch />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sistema" element={
              <ProtectedRoute><AdminDashboard /></ProtectedRoute>
            } />
            <Route path="/sistema/catastro" element={
              <ProtectedRoute anyPermission={["empresas.ver", "proveedores.ver", "empleados.ver", "localidades.ver", "configuracion_catastro.gestionar_sueldos", "configuracion_catastro.gestionar_huella"]}><CatastroPage /></ProtectedRoute>
            } />
            <Route path="/sistema/empresas" element={<Navigate to="/sistema/catastro" replace />} />
            <Route path="/sistema/presupuestos" element={
              <ProtectedRoute requiredPermission="presupuestos.ver"><VentasPage /></ProtectedRoute>
            } />
            <Route path="/sistema/usuarios" element={
              <ProtectedRoute requiredPermission="usuarios.ver"><UsuariosPage /></ProtectedRoute>
            } />
            <Route path="/sistema/config-global" element={
              <ProtectedRoute requiredPermission="configuracion_global.ver"><ConfigGlobalPage /></ProtectedRoute>
            } />
            <Route path="/sistema/perfil" element={
              <ProtectedRoute><PerfilPage /></ProtectedRoute>
            } />
            <Route path="/sistema/auditoria" element={
              <ProtectedRoute requiredPermission="auditoria.ver"><AuditoriaPage /></ProtectedRoute>
            } />
            <Route path="/sistema/inventario" element={
              <ProtectedRoute requiredPermission="inventario.ver"><InventarioPage /></ProtectedRoute>
            } />
            <Route path="/sistema/reportes" element={
              <ProtectedRoute requiredPermission="reportes.ver"><ReportesPage /></ProtectedRoute>
            } />
            <Route path="/sistema/proveedores" element={<Navigate to="/sistema/catastro" replace />} />
            <Route path="/sistema/costos-fijos" element={
              <ProtectedRoute requiredPermission="costos_fijos.ver"><CostosFijosPage /></ProtectedRoute>
            } />
            <Route path="/sistema/pagos-proveedores" element={<Navigate to="/sistema/financiero" replace />} />
            <Route path="/sistema/empleados" element={<Navigate to="/sistema/catastro" replace />} />
            <Route path="/sistema/balance" element={
              <ProtectedRoute requiredPermission="balance.ver"><BalancePage /></ProtectedRoute>
            } />
            <Route path="/sistema/ingresos-varios" element={
              <ProtectedRoute requiredPermission="ingresos_varios.ver"><IngresoVarioPage /></ProtectedRoute>
            } />
            <Route path="/sistema/egresos" element={
              <ProtectedRoute anyPermission={["compras.ver", "costos_fijos.ver", "empleados.ver", "balance.ver", "pagos_proveedores.ver", "entregas.ver"]}><EgresosPage /></ProtectedRoute>
            } />
            <Route path="/sistema/ventas" element={
              <ProtectedRoute anyPermission={["presupuestos.ver", "facturas.ver", "ingresos_varios.ver", "recibos.ver", "notas_credito.ver", "entregas.ver"]}><VentasPage /></ProtectedRoute>
            } />
            <Route path="/sistema/pdv" element={
              <ProtectedRoute requiredPermission="pdv.ver"><PDVPage /></ProtectedRoute>
            } />
            <Route path="/sistema/productos" element={
              <ProtectedRoute requiredPermission="inventario_productos.ver"><ProductosPage /></ProtectedRoute>
            } />
            <Route path="/sistema/historial-stock" element={
              <ProtectedRoute requiredPermission="historial_stock.ver"><StockHistorialPage /></ProtectedRoute>
            } />
            <Route path="/sistema/empresas-propias" element={
              <ProtectedRoute adminOnly={true}><EmpresasPropiasPage /></ProtectedRoute>
            } />
            <Route path="/sistema/bancos" element={
              <ProtectedRoute requiredPermission="bancos.ver"><BancosPage /></ProtectedRoute>
            } />
            <Route path="/sistema/plan-cuentas" element={
              <ProtectedRoute requiredPermission="plan_cuentas.ver"><PlanCuentasPage /></ProtectedRoute>
            } />
            <Route path="/sistema/soporte" element={
              <ProtectedRoute requiredPermission="soporte.ver"><SoportePage /></ProtectedRoute>
            } />
            <Route path="/sistema/financiero" element={
              <ProtectedRoute anyPermission={["transferencias.ver", "bancos.ver"]}><FinancieroPage /></ProtectedRoute>
            } />
            <Route path="/sistema/licencias" element={
              <ProtectedRoute adminOnly={true}><LicenciasPage /></ProtectedRoute>
            } />
            <Route path="/sistema/lectura-bombas" element={
              <ProtectedRoute anyPermission={["lectura_bombas.ver", "lectura_bombas.configurar"]}><LecturaBombasPage /></ProtectedRoute>
            } />
            <Route path="/sistema/produccion" element={
              <ProtectedRoute requiredPermission="produccion.ver"><ProduccionPage /></ProtectedRoute>
            } />
            <Route path="/sistema/cheques" element={<Navigate to="/sistema/financiero" replace />} />
            <Route path="/sistema/hoteleria" element={
              <ProtectedRoute requiredPermission="hoteleria.ver_panel"><HotelPage /></ProtectedRoute>
            } />
            <Route path="/sistema/loteadora" element={
              <ProtectedRoute requiredPermission="loteadora.ver"><LoteadoraPage /></ProtectedRoute>
            } />
            <Route path="/sistema/transporte" element={
              <ProtectedRoute requiredPermission="transporte.ver"><TransportePage /></ProtectedRoute>
            } />
            <Route path="/sistema/taller" element={
              <ProtectedRoute requiredPermission="taller.ver"><TallerPage /></ProtectedRoute>
            } />
            <Route path="/sistema/restaurante" element={
              <ProtectedRoute anyPermission={["restaurante.ver", "restaurante.cocina", "restaurante.mozo", "restaurante.disponibilidad", "restaurante.configurar"]}><RestaurantePage /></ProtectedRoute>
            } />
            <Route path="/sistema/importaciones" element={
              <ProtectedRoute requiredPermission="importaciones.ver"><ImportacionesPage /></ProtectedRoute>
            } />
            <Route path="/sistema/bascula" element={
              <ProtectedRoute requiredPermission="bascula.ver"><BasculaPage /></ProtectedRoute>
            } />
            <Route path="/sistema/equipos-bascula" element={
              <ProtectedRoute requiredPermission="equipos_bascula.ver"><EquiposBasculaPage /></ProtectedRoute>
            } />
            <Route path="/sistema/clinica" element={
              <ProtectedRoute requiredPermission="clinica_agenda.ver"><ClinicaPage /></ProtectedRoute>
            } />
            <Route path="/sistema/recetario" element={
              <ProtectedRoute requiredPermission="recetario.ver"><RecetarioPage /></ProtectedRoute>
            } />
            <Route path="/sistema/constructora" element={
              <ProtectedRoute requiredPermission="constructora_proyectos.ver"><ConstructoraPage /></ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
